import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  listProjectInsightsMessages,
  rerunProjectInsights,
  type SelaChatCitation,
  type SelaChatMessage,
  type SelaDecisionBrief,
  type SelaProjectDetail,
} from '../../services/selaApi';
import { getWebSocketUrl } from '../../config/websocket';
import { chatMarkdownComponents, formatChatContent } from '../../utils/chatMarkdown';
import { useAskSela, type AskSelaFocus } from './ask-sela/AskSelaContext';
import {
  EvidenceStrip,
  evidenceForRefs,
  linkCitationMarkers,
  normalizeDisplayCitations,
} from './evidence/EvidenceStrip';
import {
  websocketService,
  type WebSocketMessage,
} from '../../services/websocketService';
import { recordV2ErrorStage, recordV2FunnelStep, recordV2Latency } from '../../utils/v2Rum';

type InsightsOutletContext = {
  project: SelaProjectDetail;
  intelKickoffInProgress?: boolean;
  intelKickoffError?: string | null;
};

const POLL_INTERVAL_MS = 3000;

type StreamingState = Record<string, { content: string; citations?: SelaChatCitation[] }>;

type Turn = {
  key: string;
  user: SelaChatMessage | null;
  assistant: SelaChatMessage | null;
  kind: 'overview' | 'question' | 'refresh';
  createdAt: string;
};

function mergeMessages(
  previous: SelaChatMessage[],
  incoming: SelaChatMessage[],
): SelaChatMessage[] {
  if (!incoming.length) {
    return previous;
  }
  const byId = new Map<string, SelaChatMessage>();
  for (const message of previous) {
    if (message?.messageId) {
      byId.set(message.messageId, message);
    }
  }
  for (const message of incoming) {
    if (message?.messageId) {
      const existing = byId.get(message.messageId);
      if (existing && existing.status === 'complete' && message.status === 'pending') {
        byId.set(message.messageId, {
          ...message,
          content: existing.content || message.content,
          status: existing.status,
          citations: existing.citations?.length ? existing.citations : message.citations,
          decisionBrief: existing.decisionBrief || message.decisionBrief,
          errorMessage: existing.errorMessage || message.errorMessage,
          agentMetadata: {
            ...(message.agentMetadata || {}),
            ...(existing.agentMetadata || {}),
          },
        });
      } else if (existing && !message.content && existing.content) {
        byId.set(message.messageId, {
          ...message,
          content: existing.content,
          citations: message.citations?.length ? message.citations : existing.citations,
          decisionBrief: message.decisionBrief || existing.decisionBrief,
        });
      } else {
        byId.set(message.messageId, message);
      }
    }
  }
  return Array.from(byId.values()).sort((a, b) => {
    const left = a.messageId || '';
    const right = b.messageId || '';
    if (left === right) return 0;
    return left < right ? -1 : 1;
  });
}

function hasPendingMessage(messages: SelaChatMessage[]): boolean {
  return messages.some((message) => message.status === 'pending');
}

function hasPendingOverviewMessage(messages: SelaChatMessage[]): boolean {
  return messages.some(
    (message) => message.status === 'pending' && message.kind === 'overview',
  );
}

/** Poll GET /insights/messages only when WebSocket cannot carry the update. */
function shouldPollInsightsMessages(
  messages: SelaChatMessage[],
  options: { wsConnectionId: string | null; intelKickoffInProgress: boolean },
): boolean {
  if (options.intelKickoffInProgress) {
    return true;
  }
  if (!hasPendingMessage(messages)) {
    return false;
  }
  // Initial overview is enqueued without connection_id — worker never streams it.
  if (hasPendingOverviewMessage(messages)) {
    return true;
  }
  // Follow-ups stream over WS when we have a connection id; poll as fallback only.
  return !options.wsConnectionId;
}

function parseWebSocketRoute(url: string): { domain: string; stage: string } {
  try {
    const parsed = new URL(url);
    const pathStage = parsed.pathname.replace(/^\//, '').split('/').filter(Boolean)[0] || '';
    return { domain: parsed.host, stage: pathStage };
  } catch {
    return { domain: '', stage: '' };
  }
}

/**
 * Group ordered messages into Q&A pairs. The initial overview turn has no
 * visible user prompt; every follow-up has a user question followed by an
 * assistant reply. Pending assistant rows stay paired with their user
 * question so the UI can show them side-by-side.
 */
function buildTurns(messages: SelaChatMessage[]): Turn[] {
  const turns: Turn[] = [];
  let pendingUser: SelaChatMessage | null = null;

  for (const message of messages) {
    if (message.role === 'user') {
      if (pendingUser) {
        turns.push({
          key: pendingUser.messageId,
          user: pendingUser,
          assistant: null,
          kind: pendingUser.kind,
          createdAt: pendingUser.createdAt || pendingUser.messageId,
        });
      }
      pendingUser = message;
      continue;
    }

    const turn: Turn = {
      key: message.messageId,
      user: pendingUser,
      assistant: message,
      kind: message.kind,
      createdAt: message.createdAt || message.messageId,
    };
    turns.push(turn);
    pendingUser = null;
  }

  if (pendingUser) {
    turns.push({
      key: pendingUser.messageId,
      user: pendingUser,
      assistant: null,
      kind: pendingUser.kind,
      createdAt: pendingUser.createdAt || pendingUser.messageId,
    });
  }

  return turns;
}

function BriefSection({
  title,
  kicker,
  defaultExpanded = false,
  children,
}: {
  title: string;
  kicker?: string;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <section className="overflow-hidden rounded-2xl border border-[#dbe4f0] bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setExpanded((current) => !current)}
        className="flex w-full items-start justify-between gap-4 px-5 py-4 text-left hover:bg-[#f8fbff] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3251b7]/30"
        aria-expanded={expanded}
      >
        <div>
          {kicker ? (
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#647488]">{kicker}</p>
          ) : null}
          <h2 className="mt-1 text-lg font-bold tracking-tight text-[#0f172a]">{title}</h2>
        </div>
        <span className={`mt-1 text-[#858a9a] transition-transform ${expanded ? 'rotate-180' : ''}`}>
          ▾
        </span>
      </button>
      {expanded ? <div className="border-t border-[#eef1f6] p-5">{children}</div> : null}
    </section>
  );
}

function CollapsibleCard({
  title,
  meta,
  defaultExpanded = false,
  children,
}: {
  title: string;
  meta?: React.ReactNode;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <article className="overflow-hidden rounded-xl border border-[#e1e8f0] bg-[#fbfdff] shadow-sm">
      <button
        type="button"
        onClick={() => setExpanded((current) => !current)}
        className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3251b7]/30"
        aria-expanded={expanded}
      >
        <div className="min-w-0">
          <h3 className="font-bold text-[#0f172a]">{title}</h3>
          {meta ? <div className="mt-2 flex flex-wrap gap-2">{meta}</div> : null}
        </div>
        <span className={`mt-1 shrink-0 text-[#858a9a] transition-transform ${expanded ? 'rotate-180' : ''}`}>
          ▾
        </span>
      </button>
      {expanded ? <div className="border-t border-[#eef1f6] p-4">{children}</div> : null}
    </article>
  );
}

function CardEvidence({
  refs,
  citations,
  messageId,
  projectId,
}: {
  refs?: Array<number | string>;
  citations: SelaChatCitation[];
  messageId: string;
  projectId: string;
}) {
  const scoped = evidenceForRefs(refs, citations);
  return (
    <EvidenceStrip
      projectId={projectId}
      citations={scoped.citations}
      messageId={messageId}
      title="Evidence and sources"
      note="Sources attached to this card."
      compact
      anchorCitations={false}
      textEvidence={scoped.textEvidence}
    />
  );
}

function severityTone(value: string): string {
  if (value === 'Critical') return 'bg-red-50 text-red-800 border-red-200';
  if (value === 'High') return 'bg-orange-50 text-orange-800 border-orange-200';
  if (value === 'Medium') return 'bg-amber-50 text-amber-800 border-amber-200';
  if (value === 'Low') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  return 'bg-slate-50 text-slate-600 border-slate-200';
}

function feasibilityTone(value: string): string {
  if (value === 'Strong' || value === 'Likely') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (value === 'Conditional') return 'bg-blue-50 text-blue-700 border-blue-200';
  if (value === 'Risky') return 'bg-amber-50 text-amber-800 border-amber-200';
  if (value === 'Low' || value === 'Blocked') return 'bg-red-50 text-red-800 border-red-200';
  return 'bg-slate-50 text-slate-600 border-slate-200';
}

function Badge({ children, tone }: { children: React.ReactNode; tone: string }) {
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${tone}`}>
      {children}
    </span>
  );
}

function roadmapActionLink(
  projectId: string,
  ref: { gateId?: string; deliverableId: string },
): string {
  const params = new URLSearchParams({ deliverableId: ref.deliverableId });
  if (ref.gateId) params.set('gateId', ref.gateId);
  return `/project/${projectId}/timeline?${params.toString()}`;
}

function BriefAskButtons({
  focus,
  questions,
  onAskSela,
}: {
  focus: AskSelaFocus;
  questions: string[];
  onAskSela: (focus: AskSelaFocus, draft?: string) => void;
}) {
  return (
    <div className="mt-4 flex flex-wrap gap-2">
      {questions.map((question) => (
        <button
          key={question}
          type="button"
          onClick={() => onAskSela(focus, question)}
          className="rounded-md border border-[#cdd5e1] bg-white px-3 py-1.5 text-xs font-semibold text-[#1e4474] hover:border-[#3251b7] hover:bg-[#eff2fe]"
        >
          {question}
        </button>
      ))}
    </div>
  );
}

function DecisionBriefArtifact({
  brief,
  citations,
  messageId,
  projectId,
  projectTitle,
  onAskSela,
}: {
  brief: SelaDecisionBrief;
  citations: SelaChatCitation[];
  messageId: string;
  projectId: string;
  projectTitle: string;
  onAskSela: (focus: AskSelaFocus, draft?: string) => void;
}) {
  const { citations: displayCitations, markerMap } = normalizeDisplayCitations(citations || []);
  const linkedAnalysis = linkCitationMarkers(
    formatChatContent(brief.fullAnalysis || ''),
    displayCitations,
    messageId,
    markerMap,
  );
  const recommendation = brief.overallRecommendation;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => onAskSela(
            {
              type: 'decisionBrief',
              label: 'Decision Brief',
              sourcePage: 'insights',
              summary: projectTitle ? `Decision Brief for ${projectTitle}` : 'Decision Brief',
            },
            'What should I pay attention to in this decision brief?',
          )}
          className="rounded-md border border-[#cdd5e1] bg-white px-3 py-1.5 text-xs font-semibold text-[#1e4474] hover:border-[#3251b7] hover:bg-[#eff2fe]"
        >
          Ask Sela
        </button>
      </div>

      <BriefSection title="Overall Recommendation" defaultExpanded>
        <div className="rounded-2xl bg-[#0f172a] p-5 text-white shadow-[0_18px_45px_rgba(15,23,42,0.18)]">
          <h3 className="text-lg font-bold text-white">{recommendation.label}</h3>
          <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-300">
            {recommendation.confidence} confidence
          </p>
          <p className="mt-4 text-base leading-relaxed text-slate-100">{recommendation.rationale}</p>
          <div className="mt-5 grid gap-3 md:grid-cols-3">
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-300">Confidence reason</p>
              <p className="mt-1 text-sm text-slate-100">{recommendation.confidenceRationale}</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-300">Unresolved issue</p>
              <p className="mt-1 text-sm text-slate-100">{recommendation.mostImportantUnresolvedIssue}</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-300">Immediate posture</p>
              <p className="mt-1 text-sm text-slate-100">{recommendation.suggestedImmediatePosture}</p>
            </div>
          </div>
          <CardEvidence
            refs={recommendation.evidenceReferences}
            citations={citations}
            messageId={messageId}
            projectId={projectId}
          />
          <BriefAskButtons
            onAskSela={onAskSela}
            focus={{
              type: 'recommendation',
              label: recommendation.label,
              sourcePage: 'insights',
              summary: recommendation.rationale,
              metadata: {
                confidence: recommendation.confidence,
                unresolvedIssue: recommendation.mostImportantUnresolvedIssue,
              },
            }}
            questions={['Ask why this is Investigate', 'What would make this Pursue?', 'What should I verify first?']}
          />
        </div>
      </BriefSection>

      <BriefSection title="Development Path Feasibility Matrix">
        <div className="grid gap-3">
          {(brief.developmentPathFeasibility || []).map((row, index) => (
            <CollapsibleCard
              key={`${row.pathName}-${index}`}
              title={row.pathName}
              meta={(
                <Badge tone={feasibilityTone(row.feasibilityRating)}>{row.feasibilityRating}</Badge>
              )}
            >
              <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
                <div>
                  <p className="text-sm leading-relaxed text-[#334155]">{row.mainRationale}</p>
                  {row.keyBlockers.length ? (
                    <p className="mt-3 text-sm text-[#647488]">
                      <span className="font-semibold text-[#0f172a]">Key blockers:</span> {row.keyBlockers.join(', ')}
                    </p>
                  ) : null}
                  <p className="mt-2 text-xs font-medium text-[#647488]">Confidence: {row.confidence}</p>
                </div>
                <div className="rounded-xl border border-[#e1e8f0] bg-white p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-[#647488]">Verify next</p>
                  <p className="mt-1 text-sm font-semibold leading-relaxed text-[#334155]">{row.nextVerificationStep}</p>
                </div>
              </div>
              <CardEvidence
                refs={row.evidenceReferences}
                citations={citations}
                messageId={messageId}
                projectId={projectId}
              />
              <BriefAskButtons
                onAskSela={onAskSela}
                focus={{
                  type: 'developmentPath',
                  label: row.pathName,
                  sourcePage: 'insights',
                  summary: row.mainRationale,
                  metadata: {
                    feasibilityRating: row.feasibilityRating,
                    confidence: row.confidence,
                    keyBlockers: row.keyBlockers,
                    nextVerificationStep: row.nextVerificationStep,
                  },
                }}
                questions={[
                  `Why is ${row.pathName} ${row.feasibilityRating.toLowerCase()}?`,
                  'What would make this path stronger?',
                  'Compare ADU vs DADU for fastest permit path',
                ]}
              />
            </CollapsibleCard>
          ))}
        </div>
      </BriefSection>

      <BriefSection title="Top Deal-Killers">
        <div className="grid gap-3">
          {(brief.topDealKillers || []).map((risk, index) => (
            <CollapsibleCard
              key={`${risk.riskTitle}-${index}`}
              title={risk.riskTitle}
              meta={(
                <Badge tone={severityTone(risk.severity)}>{risk.severity}</Badge>
              )}
            >
              <p className="mt-2 text-sm leading-relaxed text-[#334155]">{risk.whyItMatters}</p>
              <p className="mt-3 text-xs text-[#647488]">
                Affects: {(risk.affectedDevelopmentPaths || []).filter(Boolean).join(', ') || 'All selected paths'}
              </p>
              <p className="mt-3 text-sm font-semibold text-[#0f172a]">Verify next: {risk.whatToVerifyNext}</p>
              {risk.whoMayNeedToBeInvolved.length ? (
                <p className="mt-2 text-xs text-[#647488]">Involve: {risk.whoMayNeedToBeInvolved.join(', ')}</p>
              ) : null}
              <CardEvidence
                refs={risk.evidenceReferences}
                citations={citations}
                messageId={messageId}
                projectId={projectId}
              />
              <BriefAskButtons
                onAskSela={onAskSela}
                focus={{
                  type: 'risk',
                  label: risk.riskTitle,
                  sourcePage: 'insights',
                  summary: risk.whyItMatters,
                  metadata: {
                    severity: risk.severity,
                    affectedDevelopmentPaths: risk.affectedDevelopmentPaths,
                    whatToVerifyNext: risk.whatToVerifyNext,
                    whoMayNeedToBeInvolved: risk.whoMayNeedToBeInvolved,
                  },
                }}
                questions={['How do I resolve this risk?', 'Who should I talk to?', 'What document would reduce uncertainty?']}
              />
            </CollapsibleCard>
          ))}
        </div>
      </BriefSection>

      <BriefSection title="Critical Missing Facts">
        <div className="grid gap-3">
          {(brief.criticalMissingFacts || []).map((fact, index) => (
            <CollapsibleCard
              key={`${fact.missingFact}-${index}`}
              title={fact.missingFact}
            >
              <p className="mt-2 text-sm leading-relaxed text-[#334155]">{fact.whyItMatters}</p>
              {fact.relatedRisk ? (
                <p className="mt-2 text-xs text-[#647488]">Related risk: {fact.relatedRisk}</p>
              ) : null}
              <p className="mt-2 text-sm text-[#334155]"><span className="font-semibold">Resolve by:</span> {fact.howToResolveIt}</p>
              <p className="mt-3 text-xs text-[#647488]">
                Affects: {(fact.affectedDevelopmentPaths || []).filter(Boolean).join(', ') || 'No specific path'}
              </p>
              <BriefAskButtons
                onAskSela={onAskSela}
                focus={{
                  type: 'missingFact',
                  label: fact.missingFact,
                  sourcePage: 'insights',
                  summary: fact.whyItMatters,
                  metadata: {
                    howToResolveIt: fact.howToResolveIt,
                    affectedDevelopmentPaths: fact.affectedDevelopmentPaths,
                    relatedRisk: fact.relatedRisk,
                  },
                }}
                questions={['What document resolves this?', 'Who should I ask?', 'Why does this matter?']}
              />
            </CollapsibleCard>
          ))}
        </div>
      </BriefSection>

      <BriefSection title="Next 5 Actions">
        <ol className="space-y-3">
          {(brief.nextActions || []).map((action, index) => (
            <li key={`${action.actionTitle}-${index}`} className="flex gap-3 rounded-xl border border-[#e1e8f0] bg-[#fbfdff] p-4">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#3251b7] text-xs font-bold text-white">
                {index + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-bold text-[#0f172a]">{action.actionTitle}</h3>
                  {action.urgency === 'Immediate' || action.urgency === 'High' ? (
                    <Badge tone={severityTone('High')}>{action.urgency}</Badge>
                  ) : null}
                </div>
                <p className="mt-1 text-sm leading-relaxed text-[#334155]">{action.reason}</p>
                {(action.relatedRiskOrMissingFact || action.relevantStage) ? (
                  <p className="mt-2 text-xs text-[#647488]">
                    {[action.relatedRiskOrMissingFact, action.relevantStage].filter(Boolean).join(' · ')}
                  </p>
                ) : null}
                {(() => {
                  const ref = action.roadmapRef || action.proposedRoadmapRef;
                  if (!ref?.deliverableId) return null;
                  const label = ref.label || ref.deliverableId;
                  return (
                    <Link
                      to={roadmapActionLink(projectId, ref)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex text-xs font-semibold text-[#3251b7] hover:underline"
                    >
                      View on roadmap: {label}
                    </Link>
                  );
                })()}
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => onAskSela({
                      type: 'nextAction',
                      label: action.actionTitle,
                      sourcePage: 'insights',
                      summary: action.reason,
                      metadata: {
                        urgency: action.urgency,
                        relatedRiskOrMissingFact: action.relatedRiskOrMissingFact,
                        relevantStage: action.relevantStage,
                      },
                    }, `What is the best way to complete "${action.actionTitle}"?`)}
                    className="rounded-md border border-[#cdd5e1] bg-white px-3 py-1.5 text-xs font-semibold text-[#1e4474] hover:border-[#3251b7]"
                  >
                    Ask Sela
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </BriefSection>

      <BriefSection title="Full Analysis">
        <div className="prose prose-sm max-w-none leading-snug">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={chatMarkdownComponents}>
            {linkedAnalysis}
          </ReactMarkdown>
        </div>
      </BriefSection>

      <BriefSection title="Evidence and Sources">
        <div className="grid gap-4 md:grid-cols-2">
          {[
            ['Confirmed facts', brief.evidenceAndSources?.confirmedFacts || []],
            ['Inferred risks', brief.evidenceAndSources?.inferredRisks || []],
            ['Missing information', brief.evidenceAndSources?.missingInformation || []],
            ['Recommended next actions', brief.evidenceAndSources?.recommendedNextActions || []],
            ['Source-backed evidence', brief.evidenceAndSources?.sourceBackedEvidence || []],
          ].map(([title, items]) => (
            <div key={title as string} className="rounded-xl border border-[#e1e8f0] bg-[#fbfdff] p-4">
              <h3 className="text-sm font-bold text-[#0f172a]">{title as string}</h3>
              {(items as string[]).length ? (
                <ul className="mt-2 space-y-1.5 text-sm leading-relaxed text-[#334155]">
                  {(items as string[]).map((item) => <li key={item}>{item}</li>)}
                </ul>
              ) : (
                <p className="mt-2 text-sm italic text-[#858a9a]">No items listed.</p>
              )}
            </div>
          ))}
        </div>
        {brief.evidenceAndSources?.evidenceGaps ? (
          <p className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900">
            {brief.evidenceAndSources.evidenceGaps}
          </p>
        ) : null}
        <EvidenceStrip projectId={projectId} citations={citations} messageId={messageId} />
      </BriefSection>
    </div>
  );
}

function PendingIndicator({ kind }: { kind: 'overview' | 'question' | 'refresh' }) {
  return (
    <div className="flex items-center gap-2 text-sm text-[#647488]">
      <span className="inline-flex gap-1">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#3251b7]" />
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#3251b7] [animation-delay:150ms]" />
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#3251b7] [animation-delay:300ms]" />
      </span>
      <span>
        {kind === 'overview'
          ? 'Preparing the decision brief. This usually takes 5–10 minutes.'
          : 'Thinking through your follow-up...'}
      </span>
    </div>
  );
}

function AssistantBody({
  assistant,
  streamingContent,
  streamingCitations,
  kind,
  projectId,
  projectTitle,
  onAskSela,
}: {
  assistant: SelaChatMessage | null;
  streamingContent: string | null;
  streamingCitations?: SelaChatCitation[];
  kind: 'overview' | 'question' | 'refresh';
  projectId: string;
  projectTitle?: string;
  onAskSela: (focus: AskSelaFocus, draft?: string) => void;
}) {
  if (!assistant) {
    return <PendingIndicator kind={kind} />;
  }
  if (assistant.status === 'error') {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
        {assistant.errorMessage || 'The agent was unable to respond to this message.'}
      </div>
    );
  }

  const liveText = streamingContent || '';
  const persistedText = formatChatContent(assistant.content);
  const isPending = assistant.status === 'pending';
  const hasLiveContent = isPending && liveText.length > 0;
  const content = hasLiveContent ? formatChatContent(liveText) : persistedText;

  const citations = streamingCitations && streamingCitations.length
    ? streamingCitations
    : assistant.citations || [];
  if (!isPending && (kind === 'overview' || kind === 'refresh') && assistant.decisionBrief) {
    return (
      <DecisionBriefArtifact
        brief={assistant.decisionBrief}
        citations={citations}
        messageId={assistant.messageId}
        projectId={projectId}
        projectTitle={projectTitle || ''}
        onAskSela={onAskSela}
      />
    );
  }
  const { citations: displayCitations, markerMap } = normalizeDisplayCitations(citations);
  const linkedContent = linkCitationMarkers(content, displayCitations, assistant.messageId, markerMap);

  return (
    <div>
      {content ? (
        <div className="prose prose-sm max-w-none leading-snug">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={chatMarkdownComponents}>
            {linkedContent}
          </ReactMarkdown>
        </div>
      ) : isPending ? (
        <PendingIndicator kind={kind} />
      ) : (
        <p className="text-sm italic text-[#858a9a]">(No response was generated.)</p>
      )}
      {!isPending && content ? (
        <EvidenceStrip projectId={projectId} citations={citations} messageId={assistant.messageId} />
      ) : null}
    </div>
  );
}

export function SelaProjectInsightsPage() {
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  const { project, intelKickoffInProgress, intelKickoffError } =
    useOutletContext<InsightsOutletContext>();
  const { openAskSela } = useAskSela();
  const projectId = projectIdParam || project.id;

  const [messages, setMessages] = useState<SelaChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rerunBusy, setRerunBusy] = useState(false);
  const [rerunError, setRerunError] = useState<string | null>(null);

  // Streaming state keyed by messageId: accumulated text + citations received over WS.
  const [streaming, setStreaming] = useState<StreamingState>({});
  const streamingRef = useRef<StreamingState>({});
  useEffect(() => {
    streamingRef.current = streaming;
  }, [streaming]);

  const [wsConnectionId, setWsConnectionId] = useState<string | null>(
    websocketService.getConnectionId(),
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const refresh = useCallback(
    async (options: { showLoading?: boolean } = {}) => {
      if (!projectId) return;
      try {
        if (options.showLoading) {
          setLoading(true);
        }
        const fresh = await listProjectInsightsMessages(projectId);
        setMessages((prev) => mergeMessages(prev, fresh));
        setLoadError(null);
      } catch (err) {
        if (options.showLoading) {
          setLoadError(err instanceof Error ? err.message : 'Failed to load insights.');
        }
      } finally {
        if (options.showLoading) {
          setLoading(false);
        }
      }
    },
    [projectId],
  );

  const handleRerunInsights = useCallback(async () => {
    if (!projectId || intelKickoffInProgress || hasPendingOverviewMessage(messages)) {
      return;
    }
    const startedAt = performance.now();
    setRerunBusy(true);
    setRerunError(null);
    recordV2FunnelStep('insights_rerun', 'start', { source_page: 'insights' }, projectId);
    let connectionId = wsConnectionId;
    try {
      if (!connectionId) {
        try {
          await websocketService.connect();
          connectionId = await websocketService.requestConnectionId();
          setWsConnectionId(connectionId);
        } catch {
          connectionId = null;
        }
      }
      const { domain, stage } = parseWebSocketRoute(getWebSocketUrl());
      const created = await rerunProjectInsights(projectId, {
        connectionId: connectionId || null,
        wsDomain: connectionId ? domain : null,
        wsStage: connectionId ? stage : null,
      });
      setMessages((prev) => mergeMessages(prev, created));
      recordV2FunnelStep('insights_rerun', 'success', { source_page: 'insights' }, projectId);
      recordV2Latency('insights_rerun', performance.now() - startedAt, {}, projectId);
    } catch (err) {
      setRerunError(err instanceof Error ? err.message : 'Failed to rerun insights.');
      recordV2FunnelStep('insights_rerun', 'failure', { source_page: 'insights' }, projectId);
      recordV2ErrorStage('insights_rerun', err, {}, projectId);
    } finally {
      setRerunBusy(false);
    }
  }, [projectId, intelKickoffInProgress, messages, wsConnectionId]);

  useEffect(() => {
    setMessages([]);
    setStreaming({});
    setRerunError(null);
    void refresh({ showLoading: true });
  }, [refresh]);

  // Keep a live WebSocket for streaming follow-up answers. We learn our own
  // connection id via the `hello` event so we can forward it to the REST
  // POST. The worker streams text_chunk events keyed by message_id.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await websocketService.connect();
        const id = await websocketService.requestConnectionId();
        if (!cancelled) setWsConnectionId(id);
      } catch {
        if (!cancelled) setWsConnectionId(null);
      }
    })();

    const textChunkHandler = (message: WebSocketMessage) => {
      const targetId = (message as any).message_id as string | undefined;
      if (!targetId) return;
      const data = (message as any).data;
      if (typeof data !== 'string' || !data) return;
      setStreaming((prev) => {
        const existing = prev[targetId] || { content: '' };
        return {
          ...prev,
          [targetId]: { ...existing, content: `${existing.content || ''}${data}` },
        };
      });
    };

    const citationsHandler = (message: WebSocketMessage) => {
      const targetId = (message as any).message_id as string | undefined;
      const citations = (message as any).citations;
      if (!targetId || !Array.isArray(citations)) return;
      setStreaming((prev) => {
        const existing = prev[targetId] || { content: '' };
        return {
          ...prev,
          [targetId]: { ...existing, citations },
        };
      });
    };

    const resultHandler = (message: WebSocketMessage) => {
      const targetId = (message as any).message_id as string | undefined;
      if (!targetId) return;
      const content = formatChatContent((message as any).content);
      const citations = Array.isArray((message as any).citations)
        ? (message as any).citations
        : undefined;
      const decisionBrief = (message as any).decisionBrief;

      setMessages((prev) => {
        const next = prev.map((m) =>
          m.messageId === targetId
            ? {
                ...m,
                content: content || m.content,
                status: 'complete' as const,
                citations: citations || m.citations,
                decisionBrief: decisionBrief || m.decisionBrief,
              }
            : m,
        );
        return next;
      });
      setStreaming((prev) => {
        if (!(targetId in prev)) return prev;
        const { [targetId]: _dropped, ...rest } = prev;
        return rest;
      });
    };

    const errorHandler = (message: WebSocketMessage) => {
      const targetId = (message as any).message_id as string | undefined;
      if (!targetId) return;
      const errorMessage = String((message as any).error || 'Agent execution failed.');
      setMessages((prev) =>
        prev.map((m) =>
          m.messageId === targetId
            ? { ...m, status: 'error' as const, errorMessage }
            : m,
        ),
      );
      setStreaming((prev) => {
        if (!(targetId in prev)) return prev;
        const { [targetId]: _dropped, ...rest } = prev;
        return rest;
      });
    };

    websocketService.onMessage('text_chunk', textChunkHandler);
    websocketService.onMessage('citations', citationsHandler);
    websocketService.onMessage('result', resultHandler);
    websocketService.onMessage('error', errorHandler);

    return () => {
      cancelled = true;
      websocketService.offMessage('text_chunk', textChunkHandler);
      websocketService.offMessage('citations', citationsHandler);
      websocketService.offMessage('result', resultHandler);
      websocketService.offMessage('error', errorHandler);
    };
  }, [refresh]);

  const shouldPoll = useMemo(
    () =>
      shouldPollInsightsMessages(messages, {
        wsConnectionId,
        intelKickoffInProgress: Boolean(intelKickoffInProgress),
      }),
    [messages, wsConnectionId, intelKickoffInProgress],
  );

  useEffect(() => {
    if (!shouldPoll) return;
    const interval = window.setInterval(() => {
      void refresh();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [refresh, shouldPoll]);

  const turns = useMemo(() => buildTurns(messages), [messages]);
  const overviewTurns = useMemo(() => turns.filter((turn) => turn.kind === 'overview'), [turns]);
  const visibleOverviewTurns = useMemo(() => {
    const pending = overviewTurns.filter((turn) => turn.assistant?.status === 'pending');
    const latestComplete =
      overviewTurns
        .filter((turn) => turn.assistant?.status === 'complete' && turn.assistant?.decisionBrief)
        .find((turn) => (turn.assistant?.agentMetadata as any)?.isLatestBrief)
      || [...overviewTurns]
        .reverse()
        .find((turn) => turn.assistant?.status === 'complete' && turn.assistant?.decisionBrief);
    return [...(latestComplete ? [latestComplete] : []), ...pending.filter((turn) => turn.key !== latestComplete?.key)];
  }, [overviewTurns]);
  const latestBriefUpdatedAt = visibleOverviewTurns.find((turn) => turn.assistant?.status === 'complete')?.assistant?.updatedAt;

  const overviewPending =
    intelKickoffInProgress ||
    messages.some((message) => message.kind === 'overview' && message.status === 'pending');
  const isEmpty = !loading && turns.length === 0;

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [turns.length]);

  return (
    <main className="flex min-h-0 flex-1 flex-col bg-[#f7fafd]">
      <div ref={scrollRef} className="flex-1 overflow-auto px-6 py-6 md:px-8">
        <div className="mx-auto mb-5 max-w-7xl">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-[#0f172a] md:text-3xl">Insights</h1>
              <p className="mt-1 text-sm text-[#647488]">
                Decision guidance, feasibility signals, risks, missing facts, next actions, and evidence.
              </p>
              {latestBriefUpdatedAt ? (
                <p className="mt-1 text-xs font-semibold text-[#647488]">
                  Last updated {new Date(latestBriefUpdatedAt).toLocaleString()}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => void handleRerunInsights()}
              disabled={rerunBusy || overviewPending}
              className="inline-flex items-center justify-center rounded-md border border-[#cdd5e1] bg-white px-3 py-2 text-xs font-semibold text-[#1e4474] hover:border-[#3251b7] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {rerunBusy ? 'Rerunning...' : overviewPending ? 'Insights running' : 'Rerun insights'}
            </button>
          </div>
        </div>
        {rerunError ? (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {rerunError}
          </div>
        ) : null}
        {intelKickoffError ? (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {intelKickoffError}
          </div>
        ) : null}
        {loading ? (
          <div className="flex h-full items-center justify-center text-sm text-[#647488]">
            Loading insights...
          </div>
        ) : loadError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {loadError}
          </div>
        ) : isEmpty ? (
          <div className="mx-auto max-w-2xl rounded-xl border border-dashed border-[#e1e8f0] bg-white px-6 py-10 text-center text-sm text-[#647488]">
            {overviewPending
              ? 'Preparing the decision brief. This usually takes 5–10 minutes.'
              : 'The Decision Brief will appear here once research has run for this project.'}
          </div>
        ) : (
          <div className="mx-auto grid max-w-7xl gap-5">
            <div className="flex min-w-0 flex-col gap-5">
              {visibleOverviewTurns.map((turn) => {
                const streamingEntry = turn.assistant
                  ? streaming[turn.assistant.messageId]
                  : undefined;
                return (
                  <div key={turn.key} className="min-w-0">
                    <AssistantBody
                      assistant={turn.assistant}
                      streamingContent={streamingEntry?.content ?? null}
                      streamingCitations={streamingEntry?.citations}
                      kind="overview"
                      projectId={projectId}
                      projectTitle={project.title}
                      onAskSela={openAskSela}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

export default SelaProjectInsightsPage;
